import React, { useState, useEffect, useRef, useMemo } from 'react';
import { emailApiService, EmailTemplate, EmailTemplateCategory } from '../../services/email/emailApiService';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../foundation/altzero_base/base/components/ui/table';
import { Pencil, Trash2, Plus, FileText, ArrowLeft, Eye, Code2, ChevronDown, ChevronUp, Copy, Check, Zap, Search, Users, Building2, Globe } from 'lucide-react';
import { useToast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import { Switch } from '../../../../foundation/altzero_base/base/components/ui/switch';
import { EMAIL_PLACEHOLDERS, PLACEHOLDER_CATEGORIES } from '../../utils/emailPlaceholders';
import { PaginationBar } from './PaginationBar';

const PAGE_SIZE = 20;

const CATEGORY_OPTIONS: { value: EmailTemplateCategory; label: string; description: string }[] = [
    { value: 'retailer_rfi', label: 'Retailer RFI', description: 'Used when sending RFI emails to retailers' },
    { value: 'client_proposal', label: 'Client Proposal', description: 'Used when sending proposals to clients' },
    { value: 'bill_validation', label: 'Bill Validation', description: 'The covering email that carries a bill validation report PDF' },
    { value: 'general', label: 'General', description: 'Available in both retailer and client email dialogs' },
];

const CATEGORY_FILTER_TABS: { value: 'all' | EmailTemplateCategory; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'retailer_rfi', label: 'Retailer RFI' },
    { value: 'client_proposal', label: 'Client Proposal' },
    { value: 'bill_validation', label: 'Bill Validation' },
    { value: 'general', label: 'General' },
];

// ── Placeholder Reference Panel ──────────────────────────────────────────────

type PlaceholderReferencePanelProps = {
    onInsert?: (placeholder: string) => void;
};

const PlaceholderReferencePanel: React.FC<PlaceholderReferencePanelProps> = ({ onInsert }) => {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);

    const handleClick = (text: string) => {
        if (onInsert) {
            onInsert(text);
            setCopied(text);
            setTimeout(() => setCopied(null), 1500);
        } else {
            navigator.clipboard.writeText(text);
            setCopied(text);
            setTimeout(() => setCopied(null), 1500);
        }
    };

    return (
        <div className="border rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/70 transition-colors text-sm font-medium"
            >
                <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-primary" />
                    Available Placeholders — {onInsert ? 'click to insert' : 'click to copy'}
                </span>
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {open && (
                <div className="p-4 space-y-5 bg-background">
                    {PLACEHOLDER_CATEGORIES.map(cat => {
                        const items = EMAIL_PLACEHOLDERS.filter(p => p.category === cat);
                        return (
                            <div key={cat}>
                                <p className="text-[11px] font-700 uppercase tracking-widest text-muted-foreground mb-2">
                                    {cat}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {items.map(p => (
                                        <button
                                            key={p.placeholder}
                                            type="button"
                                            title={`${p.description}${p.autoFilled ? ' · Auto-filled' : ' · Edit manually'}`}
                                            onClick={() => handleClick(p.placeholder)}
                                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono border transition-colors
                                                ${p.autoFilled
                                                    ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                                                    : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                                                }`}
                                        >
                                            {copied === p.placeholder
                                                ? <Check className="h-3 w-3" />
                                                : <Copy className="h-3 w-3" />
                                            }
                                            {p.placeholder}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    <p className="text-[11px] text-muted-foreground pt-1">
                        <span className="inline-block w-2.5 h-2.5 rounded bg-blue-200 mr-1 align-middle" />Blue = auto-filled from tender data &nbsp;
                        <span className="inline-block w-2.5 h-2.5 rounded bg-amber-200 mr-1 align-middle" />Amber = edit manually before sending
                    </p>
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────

const EmailTemplateManager: React.FC = () => {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<'all' | EmailTemplateCategory>('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [previewMode, setPreviewMode] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState<Partial<EmailTemplate>>({
        name: '',
        subject: '',
        body_html: '',
        is_default: false,
        category: 'general'
    });
    const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);
    const { toast } = useToast();

    const handleInsertPlaceholder = (placeholder: string) => {
        // Insert at cursor position in HTML textarea
        const ta = htmlTextareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const current = currentTemplate.body_html || '';
        const updated = current.slice(0, start) + placeholder + current.slice(end);
        setCurrentTemplate({ ...currentTemplate, body_html: updated });
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(start + placeholder.length, start + placeholder.length);
        });
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        setIsLoading(true);
        try {
            const data = await emailApiService.getTemplates();
            setTemplates(data);
        } catch (error) {
            console.error('Failed to load templates:', error);
            toast({ title: 'Error', description: 'Failed to load email templates', variant: 'destructive' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!currentTemplate.name || !currentTemplate.subject || !currentTemplate.body_html) {
            toast({ title: 'Validation Error', description: 'Please fill in all required fields', variant: 'destructive' });
            return;
        }
        try {
            if (currentTemplate.id) {
                await emailApiService.updateTemplate(currentTemplate.id, currentTemplate as any);
                toast({ title: 'Success', description: 'Template updated successfully' });
            } else {
                await emailApiService.createTemplate(currentTemplate as any);
                toast({ title: 'Success', description: 'Template created successfully' });
            }
            setViewMode('list');
            loadTemplates();
        } catch (error) {
            console.error('Failed to save template:', error);
            toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' });
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this template?')) return;
        try {
            await emailApiService.deleteTemplate(id);
            toast({ title: 'Success', description: 'Template deleted successfully' });
            loadTemplates();
        } catch (error) {
            console.error('Failed to delete template:', error);
            toast({ title: 'Error', description: 'Failed to delete template', variant: 'destructive' });
        }
    };

    const filteredTemplates = useMemo(() => {
        let result = templates;
        if (categoryFilter !== 'all') {
            result = result.filter(t => (t.category || 'general') === categoryFilter);
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            result = result.filter(t =>
                t.name?.toLowerCase().includes(q) ||
                t.subject?.toLowerCase().includes(q)
            );
        }
        return result;
    }, [templates, search, categoryFilter]);

    // Reset page when search or filter changes
    useEffect(() => { setPage(1); }, [search, categoryFilter]);

    const paginatedTemplates = useMemo(() =>
        filteredTemplates.slice((page - 1) * pageSize, page * pageSize),
        [filteredTemplates, page, pageSize]
    );

    const openEditor = (template?: EmailTemplate) => {
        if (template) {
            setCurrentTemplate(template);
        } else {
            setCurrentTemplate({ name: '', subject: '', body_html: '', is_default: false, category: 'general' });
        }
        setPreviewMode(false);
        setViewMode('edit');
    };

    // ── Edit view ─────────────────────────────────────────────────────────────
    if (viewMode === 'edit') {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => setViewMode('list')}>
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Templates
                    </Button>
                    <h2 className="text-2xl font-bold tracking-tight">
                        {currentTemplate.id ? 'Edit Template' : 'Create Template'}
                    </h2>
                </div>

                <Card>
                    <CardContent className="pt-6 space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Template Name</Label>
                                <Input
                                    id="name"
                                    value={currentTemplate.name}
                                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, name: e.target.value })}
                                    placeholder="e.g. Standard RFI"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="subject">Email Subject</Label>
                                <Input
                                    id="subject"
                                    value={currentTemplate.subject}
                                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, subject: e.target.value })}
                                    placeholder="e.g. {{Entity Name}} | Pricing Due {{Pricing Due Date}}"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 items-start">
                            <div className="space-y-2">
                                <Label>Category</Label>
                                <select
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                    value={currentTemplate.category || 'general'}
                                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, category: e.target.value as EmailTemplateCategory })}
                                >
                                    {CATEGORY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label} — {opt.description}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center space-x-2 pt-7">
                                <Switch
                                    id="is-default"
                                    checked={currentTemplate.is_default}
                                    onCheckedChange={(checked) => setCurrentTemplate({ ...currentTemplate, is_default: checked })}
                                />
                                <Label htmlFor="is-default">Set as default for this category</Label>
                            </div>
                        </div>

                        {/* Placeholder reference — always visible in editor */}
                        <PlaceholderReferencePanel onInsert={handleInsertPlaceholder} />

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Email Body</Label>
                                <div className="flex items-center rounded-md border overflow-hidden text-xs">
                                    <button
                                        type="button"
                                        onClick={() => setPreviewMode(false)}
                                        className={`flex items-center gap-1 px-3 py-1.5 transition-colors ${!previewMode ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                    >
                                        <Code2 className="h-3 w-3" /> Edit HTML
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPreviewMode(true)}
                                        className={`flex items-center gap-1 px-3 py-1.5 transition-colors ${previewMode ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                    >
                                        <Eye className="h-3 w-3" /> Preview
                                    </button>
                                </div>
                            </div>

                            {previewMode ? (
                                <iframe
                                    srcDoc={/<!DOCTYPE\s+html|<html\b/i.test(currentTemplate.body_html || '') ? currentTemplate.body_html! : `<!DOCTYPE html><html><body style="font-family:'Segoe UI',Arial,sans-serif;padding:24px;margin:0;">${currentTemplate.body_html || ''}</body></html>`}
                                    title="Template preview"
                                    className="w-full border rounded-md bg-white"
                                    style={{ height: '500px' }}
                                    sandbox="allow-same-origin"
                                />
                            ) : (
                                <textarea
                                    ref={htmlTextareaRef}
                                    className="w-full min-h-[400px] border rounded-md p-3 font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-ring bg-muted/30"
                                    value={currentTemplate.body_html || ''}
                                    onChange={(e) => setCurrentTemplate({ ...currentTemplate, body_html: e.target.value })}
                                    spellCheck={false}
                                />
                            )}

                            <p className="text-xs text-muted-foreground">
                                Paste or write full HTML. Use {`{{Placeholder}}`} syntax — click from the reference above to insert at cursor.
                            </p>
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setViewMode('list')}>Cancel</Button>
                            <Button onClick={handleSave}>Save Template</Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // ── List view ─────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold tracking-tight">Email Templates</h2>
                <Button onClick={() => openEditor()}>
                    <Plus className="mr-2 h-4 w-4" /> Create Template
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-4">
                        <CardTitle>Manage Templates</CardTitle>
                        <div className="relative w-64">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name or subject..."
                                className="pl-8 h-9"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex gap-1 mt-2">
                        {CATEGORY_FILTER_TABS.map(tab => (
                            <button
                                key={tab.value}
                                type="button"
                                onClick={() => setCategoryFilter(tab.value)}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                    categoryFilter === tab.value
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted hover:bg-muted/70 text-muted-foreground'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Subject</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Default</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        Loading templates…
                                    </TableCell>
                                </TableRow>
                            ) : filteredTemplates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        {search ? 'No templates match your search.' : 'No templates found. Create one to get started.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedTemplates.map((template) => (
                                    <TableRow key={template.id}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center">
                                                <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                                                {template.name}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">{template.subject}</TableCell>
                                        <TableCell>
                                            {(() => {
                                                const cat = template.category || 'general';
                                                const styles: Record<string, string> = {
                                                    retailer_rfi: 'bg-blue-50 border-blue-200 text-blue-700',
                                                    client_proposal: 'bg-purple-50 border-purple-200 text-purple-700',
                                                    general: 'bg-gray-50 border-gray-200 text-gray-600',
                                                };
                                                const icons: Record<string, React.ReactNode> = {
                                                    retailer_rfi: <Users className="h-3 w-3" />,
                                                    client_proposal: <Building2 className="h-3 w-3" />,
                                                    general: <Globe className="h-3 w-3" />,
                                                };
                                                const labels: Record<string, string> = {
                                                    retailer_rfi: 'Retailer RFI',
                                                    client_proposal: 'Client Proposal',
                                                    general: 'General',
                                                };
                                                return (
                                                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${styles[cat]}`}>
                                                        {icons[cat]}{labels[cat]}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {template.is_default && (
                                                <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-primary text-primary-foreground">
                                                    Default
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => openEditor(template)}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(template.id)} className="text-destructive hover:text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    {filteredTemplates.length > 0 && (
                        <PaginationBar
                            page={page}
                            pageSize={pageSize}
                            total={filteredTemplates.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                        />
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default EmailTemplateManager;
